import { useEffect } from "preact/hooks";
import { effect } from "@preact/signals";
import { AboutDialog, openAbout } from "./components/AboutDialog";
import { DownloadDialog } from "./components/DownloadDialog";
import { DesignLab } from "./components/DesignLab";
import { MapView } from "./components/Map";
import { FilterPanel } from "./components/FilterPanel";
import { CountBar } from "./components/CountBar";
import { UnifiedList } from "./components/UnifiedList";
import { MentionsReader, MentionsTrigger } from "./components/MentionsReader";
import { ExplorePage } from "./components/ExplorePage";
import { loadAllData } from "./data/loader";
import { initFromURL, startURLSync } from "./state/url-sync";
import { theme, toggleTheme, meta, viewMode, dataError } from "./state/store";

/** One scrolling document, v1-style: header → filters → map → count bar → list. The map
 *  scrolls away like any other content; the sticky count bar is the only fixed chrome
 *  while reading. Explore swaps the whole browse page out. */
export function App() {
  // Initialize URL state and data
  useEffect(() => {
    initFromURL({ coldLoad: true });
    startURLSync();
    loadAllData().catch((err) => {
      console.error("Failed to load data:", err);
    });
  }, []);

  // Sync theme to document. theme-color must track the HEADER surface (--bg-secondary),
  // not the accent: mobile browsers paint the strip around the address bar with it, and
  // a mismatched value shows up as a colored seam between browser chrome and the header
  // once the URL bar has collapsed and re-expanded mid-scroll.
  useEffect(() => {
    const dispose = effect(() => {
      document.documentElement.dataset.theme = theme.value;
      document
        .querySelector('meta[name="theme-color"]')
        ?.setAttribute("content", theme.value === "dark" ? "#161d2f" : "#ffffff");
    });
    return dispose;
  }, []);

  const mode = viewMode.value;
  const isDark = theme.value === "dark";
  const exported = meta.value?.exported_at;
  const loadIssue = dataError.value;

  return (
    <div class="app">
      <header class="app-header">
        <a class="app-title" href="/" aria-label="Classical Concert Map — home">
          Classical Concert Map
        </a>
        <nav class="app-nav">
          <button
            class={`mode-btn ${mode === "browse" ? "active" : ""}`}
            onClick={() => {
              viewMode.value = "browse";
            }}
            type="button"
            aria-pressed={mode === "browse"}
          >
            Concerts
          </button>
          <button
            class={`mode-btn ${mode === "explore" ? "active" : ""}`}
            onClick={() => {
              viewMode.value = "explore";
            }}
            type="button"
            aria-pressed={mode === "explore"}
          >
            Explore
          </button>
          <button
            class="theme-btn"
            onClick={toggleTheme}
            type="button"
            aria-label={`Switch to ${isDark ? "light" : "dark"} mode`}
            title={`Switch to ${isDark ? "light" : "dark"} mode`}
          >
            {isDark ? "☾" : "☀"}
          </button>
        </nav>
      </header>

      {loadIssue && (
        <div class="setup-notice" role="alert">
          {loadIssue}
        </div>
      )}

      <main class="app-main">
        {mode === "explore" ? (
          <ExplorePage />
        ) : (
          <>
            <FilterPanel />
            <div class="map-pane">
              <MapView />
            </div>
            <CountBar />
            <MentionsTrigger />
            <UnifiedList />
          </>
        )}
      </main>

      <MentionsReader />

      <DesignLab />


      <footer class="app-footer">
        <span>
          Live listings from venue websites · archives: NY Philharmonic &amp; Carnegie Hall
          (CC0) + Wikidata premieres
        </span>
        <span class="footer-right">
          <button type="button" class="footer-about" onClick={openAbout}>
            About
          </button>
          <a class="footer-about" href="/api-docs/">
            Data &amp; API
          </a>
          {exported && (
            <span class="footer-updated">
              Data updated {new Date(exported).toLocaleDateString()}
            </span>
          )}
        </span>
      </footer>

      <AboutDialog />
      <DownloadDialog />
    </div>
  );
}
